import {
  computed,
  defineComponent,
  h,
  inject,
  provide,
  ref,
  type Ref,
} from 'vue'

interface AccordionContext {
  activeItem: Ref<string>
  changeActiveIndex: (id: string) => void
}

const AccordionContext = Symbol('AccordionContext')

const useAccordionContext = (component: string): AccordionContext => {
  const context = inject(AccordionContext, null)

  if (context === null) {
    throw new Error(`<${component}> is missing parent <Accordion /> component`)
  }

  return context
}

export const Accordion = defineComponent({
  name: 'Accordion',
  setup(_, { slots }) {
    const api: AccordionContext = {
      activeItem: ref(''),
      changeActiveIndex(id: string) {
        api.activeItem.value = id
      },
    }

    provide(AccordionContext, api)

    return () =>
      h('div', { class: 'w-full h-full flex flex-col' }, slots.default?.())
  },
})

export const AccordionItem = defineComponent({
  name: 'AccordionItem',
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  setup(props, { slots }) {
    const api = useAccordionContext('AccordionItem')
    const isActiveItem = computed(() => props.id === api.activeItem.value)

    if (!api.activeItem.value) {
      api.activeItem.value = props.id
    }

    return () =>
      h(
        'div',
        {
          class: `flex flex-col h-[50px] bg-white z-10 border-t border-[#E5E5E5] transition-all duration-300 ease-[ease] ${
            isActiveItem.value && 'grow'
          }`,
          onClick: () => api.changeActiveIndex(props.id),
        },
        slots.default?.()
      )
  },
})

export const AccordionHeader = defineComponent({
  name: 'AccordionHeader',
  setup:
    (_, { slots }) =>
    () =>
      h(
        'div',
        {
          class:
            'w-full px-4 py-[15px] flex items-center font-primary font-bold text-[14px] leading-[20px] cursor-pointer',
        },
        slots.default?.()
      ),
})

export const AccordionContent = defineComponent({
  name: 'AccordionContent',
  setup(_, { slots }) {
    return () =>
      h('div', { class: 'px-4 font-primary text-gray-800' }, slots.default?.())
  },
})
